import { Injectable } from "@angular/core";
import { tap } from "rxjs";
import { DateTime } from "luxon";
import { createStore, getRegistry, select, withProps } from "@ngneat/elf";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";

import { SessionDataService } from "../services/session.service";
import { environment } from "../../../environments/environment";
import { selectLoading, withLoading } from "../store/stateprops";
import { initializeRequestElf } from "../store/state";
import { UserInfo } from "../models/user.model";
import { LoginResult, TestLoginRequest } from "../models/auth.model";

export interface SessionStoreProps {
    accessToken: string | null;
    tokenExpireTime: string | null;
    synlabId: string | null;
    userInfo: UserInfo | null;
}

@Injectable({ providedIn: "root" })
export class SessionStoreRepository {
    private static readonly schemaAccessKeyStorageKey = "schemaAccessKey";

    private store = this.createStore();
    private logoutInProgress = false;

    constructor(
        private readonly sessionDataService: SessionDataService,
    ) { }

    isLoggedIn$ = this.store.pipe(select(() => this.isLoggedIn()));
    isLoading$ = this.store.pipe(selectLoading());
    isPatient$ = this.store.pipe(select((x) => x.userInfo?.providerNumber === null));
    userFullName$ = this.store.pipe(select((x) => x.userInfo?.firstName + " " + this.userInfo?.lastName));
    userNameInit$ = this.store.pipe(
        select((x) => {
            const firstName = x.userInfo?.firstName || "";
            const lastNameInitial = x.userInfo?.lastName ? x.userInfo.lastName.substring(0, 1) + "." : "";
            return `${firstName} ${lastNameInitial}`;
        })
    );

    get accessToken() {
        return this.store.getValue().accessToken;
    }
    get synlabId() {
        return this.store.getValue().synlabId;
    }
    get userInfo() {
        return this.store.getValue().userInfo;
    }

    get isPatient(): boolean {
        return this.store.getValue().userInfo?.providerNumber === null;
    }

    isLoggedInAsync = this.store.pipe(select(() => this.isLoggedIn()));

    isLoggedIn(): boolean {
        const token = this.store.getValue().accessToken;
        const expireTime = this.store.getValue().tokenExpireTime;
        const tokenExpireTime = expireTime ? DateTime.fromISO(expireTime) : DateTime.now();

        if (token === "" || token === null || tokenExpireTime < DateTime.now()) {
            return false;
        }

        return true;
    }

    resetStores() {
        getRegistry().forEach((store) => {
            store.reset()
        });
    }

    storeSchemaAccessKey(schemaAccessKey: string) {
        sessionStorage.setItem(SessionStoreRepository.schemaAccessKeyStorageKey, schemaAccessKey);
    }

    popSchemaAccessKey(): string | null {
        const schemaAccessKey = sessionStorage.getItem(SessionStoreRepository.schemaAccessKeyStorageKey);
        sessionStorage.removeItem(SessionStoreRepository.schemaAccessKeyStorageKey);
        return schemaAccessKey;
    }

    redirectToStsLoginPage() {
        const params =
            "response_type=" +
            environment.authConfig.responseType +
            "&client_id=" +
            environment.authConfig.clientId +
            "&redirect_uri=" +
            environment.authConfig.redirectUri +
            "/auth/signin" +
            "&scope=" +
            environment.authConfig.scopes;
            
        const idp = environment.authConfig.idp ? "&IdP=" + environment.authConfig.idp : "";
        const loginUrl = environment.authConfig.issuer + "/Account/Auth?AuthParam=" + encodeURIComponent(params) + idp;

        window.location.href = loginUrl;
    }

    handleSessionExpired(message: string) {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;

        alert(message);
        this.redirectToStsLogoutPage();
    }

    redirectToStsLogoutPage() {
        const params = "post_logout_redirect_uri=" + environment.authConfig.redirectUri + "/auth/logged-out";
        const stsLogoutUrl =
            environment.authConfig.issuer + "/Account/Endsession?EndSessionParam=" + encodeURIComponent(params);

        window.location.href = stsLogoutUrl;
    }

    redirectToStsValidateCprPage() {
        const validateCprUrl =
            environment.authConfig.validateCprUrl + "?redirect=" + environment.authConfig.redirectUri + "/auth/login";

        window.location.href = validateCprUrl;
    }

    login$(code: string, schemaAccessKey?: string | null) {
        return this.sessionDataService
            .login$({
                code,
                redirectUri: environment.authConfig.redirectUri + "/auth/signin",
                schemaAccessKey,
            })
            .pipe(
                initializeRequestElf(this.store),
                tap({
                    next: (loginResponse: LoginResult) => {
                        this.store.update((state) => ({
                            ...state,
                            accessToken: loginResponse.jwtToken,
                            tokenExpireTime: DateTime.now()
                                .plus({ seconds: loginResponse.expiresIn ?? 0 })
                                .toISO(),
                            synlabId: loginResponse.synlabId,
                        }));
                        this.store.update((state) => ({
                            ...state,
                            userInfo: loginResponse.userInfo,
                        }));
                    },
                })
            );
    }

    testlogin$(testloginOption: string, req: TestLoginRequest) {
        return this.sessionDataService
            .testlogin(testloginOption, req)!
            .pipe(
                initializeRequestElf(this.store),
                tap({
                    next: (loginResponse) => {
                        this.store.update((state) => ({
                            ...state,
                            accessToken: loginResponse.jwtToken,
                            tokenExpireTime: DateTime.now()
                                .plus({ seconds: loginResponse.expiresIn ?? 0 })
                                .toISO(),
                            synlabId: loginResponse.synlabId,
                        }));
                        this.store.update((state) => ({
                            ...state,
                            userInfo: loginResponse.userInfo,
                        }));
                    },
                })
            )
    }

    doctorLoginWithGuid$(testloginOption: string) {
        return this.sessionDataService
            .doctorloginGuid(testloginOption)!
            .pipe(
                initializeRequestElf(this.store),
                tap({
                    next: (loginResponse) => {
                        this.store.update((state) => ({
                            ...state,
                            accessToken: loginResponse.jwtToken,
                            tokenExpireTime: DateTime.now()
                                .plus({ seconds: loginResponse.expiresIn ?? 0 })
                                .toISO(),
                            synlabId: loginResponse.synlabId,
                        }));
                        this.store.update((state) => ({
                            ...state,
                            userInfo: loginResponse.userInfo,
                        }));
                    },
                })
            )
    }

    updateStore(loginResponse: LoginResult){ 
        this.store.update((state) => ({
            ...state,
            accessToken: loginResponse.jwtToken,
            tokenExpireTime: DateTime.now()
                .plus({ seconds: loginResponse.expiresIn ?? 0 })
                .toISO(),
            synlabId: loginResponse.synlabId,
            userInfo: loginResponse.userInfo,
        }));
    }

    private createStore(): typeof store {
        const store = createStore(
            { name: "sessionStore" },
            withProps<SessionStoreProps>({
                accessToken: null,
                tokenExpireTime: null,
                synlabId: null,
                userInfo: null,
            }),
            withLoading()
        );

        persistState(store, {
            storage: localStorageStrategy,
        });

        return store;
    }

}